import * as zod from 'zod';

type EndEffectorTCP = `ee-${string}`;

export function isEndEffectorTCP(value: unknown): value is EndEffectorTCP {
  return typeof value === 'string' && value.startsWith('ee-');
}

/**
 * Strict variant: parsing an unrecognized value fails instead of degrading
 * to 'wrist'. For boundaries where the option is motion identity — e.g. the
 * Set Active Tooltip step's runner arguments, where "exactly this tooltip"
 * silently becoming the flange is a wrong-motion bug, not a tolerable
 * downgrade.
 */
export const TCPOffsetOptionStrict = zod.preprocess(
  (value) => {
    // legacy endeffector tcp options were raw numbers
    return typeof value === 'number' ? `ee-${value}` : value;
  },
  // union of valid options
  //
  // The end-effector branch is a predicate, so it has no JSON-schema shape and
  // any renderer reading this schema would drop it. The description is the text
  // such a renderer prints in its place; it names the accepted shape rather
  // than describing it, because it stands in for a type, not for prose.
  zod.union([
    zod.literal('wrist'),
    zod.literal('wristCamera'),
    // `${string}` here is type text, not an interpolation that failed to run.
    // eslint-disable-next-line no-template-curly-in-string
    zod.custom<EndEffectorTCP>(isEndEffectorTCP).describe('`ee-${string}`'),
  ]),
);

export const TCPOffsetOption = TCPOffsetOptionStrict
  // if we add more options later,
  // the catch will let us downgrade with fewer problems
  .catch('wrist');

export type TCPOffsetOption = zod.infer<typeof TCPOffsetOption>;

export const DEFAULT_TCP_OFFSET_OPTION: TCPOffsetOption = 'wrist';

/**
 * A tooltip named outright, or `'auto'` — the tooltip the target pose was
 * taught with. The editor calls `'auto'` "Saved tooltip" under
 * `setActiveTooltip` and "Automatic" without it; the stored spelling stays
 * `'auto'` either way, so software predating that label resolves the pose the
 * same way.
 *
 * What `'auto'` falls back to when the pose recorded no tooltip belongs to the
 * field holding it, not to this schema. Read the resolution at the field.
 */
export const TCPOptionOrAuto = zod.literal('auto').or(TCPOffsetOption);

export type TCPOptionOrAuto = zod.infer<typeof TCPOptionOrAuto>;

/**
 * A motion child's tooltip selection: a frame named outright, or one of three
 * symbolic members that name a frame only once the routine runs. They differ in
 * what they read, not in when they resolve.
 *
 * - `'auto'` -- the tooltip the target pose was taught with, shown as "Saved
 *   tooltip".
 * - `'active'` -- the robot's Active Tooltip, captured at the enclosing move's
 *   start.
 * - `'parent'` -- the enclosing Move step's tooltip.
 *
 * Only a waypoint or an arc can hold one: they are the only steps resolved
 * against an enclosing Move step and a captured Active Tooltip. Everything else
 * that stores a tooltip takes `TCPOptionOrAuto`, where the two new members would
 * name nothing.
 *
 * What a member falls back to when it names nothing belongs to the field holding
 * it, not to this schema: on a Waypoint or Arc a relative-to-current target
 * answers with the frame it reads its carried pose in, so the read and the
 * command stay matched. Read the resolution at the field.
 *
 * Only `'auto'` is safe to store on a robot that predates these members:
 * `TCPOffsetOption` ends in `.catch('wrist')`, so an older build reads
 * `'active'` or `'parent'` as the wrist flange.
 *
 * Built on `TCPOptionOrAuto` rather than on `TCPOffsetOption` directly. The
 * agent-facing DSL reference renders the end-effector branch from a `$ref` that
 * zod-to-json-schema points at the first place the shared subschema is
 * serialized; reaching past `TCPOptionOrAuto` moves that anchor and the branch
 * renders as `unknown`.
 */
export const MotionChildTCPOption = zod
  .literal('active')
  .or(zod.literal('parent'))
  .or(TCPOptionOrAuto);

export type MotionChildTCPOption = zod.infer<typeof MotionChildTCPOption>;

/** The three members that name a frame only once the routine runs. */
export const SYMBOLIC_TOOLTIPS = ['auto', 'active', 'parent'] as const;

export type SymbolicTooltip = (typeof SYMBOLIC_TOOLTIPS)[number];

export function isSymbolicTooltip(
  tcpOption: MotionChildTCPOption,
): tcpOption is SymbolicTooltip {
  return SYMBOLIC_TOOLTIPS.includes(tcpOption as SymbolicTooltip);
}

/**
 * A fully-determined TCP frame: the option name plus, when known, the exact
 * equipment instance that defines it. `gripperID` matters because an `ee-*`
 * name alone under-determines the frame — several equipment rows can share a
 * kind (so the name is ambiguous), and device kinematics fall back to their
 * own offset for foreign `ee-*` names — while resolution by id works even for
 * disabled equipment. `gripperID: undefined` means "resolve the name against
 * enabled equipment", exactly like command consumption without a gripperID.
 *
 * Strict on the option: a frame is motion identity wherever it is parsed —
 * persisted user intent restored at boot, set-active-tooltip packets — so an
 * unrecognized name fails the parse instead of silently becoming the wrist.
 */
export const TCPFrame = zod.object({
  tcpOption: TCPOffsetOptionStrict,
  gripperID: zod.string().optional(),
});

export type TCPFrame = zod.infer<typeof TCPFrame>;

export function tcpFramesEqual(a: TCPFrame, b: TCPFrame): boolean {
  return a.tcpOption === b.tcpOption && a.gripperID === b.gripperID;
}

/**
 * A Move step's tooltip: either the robot's Active Tooltip at run time, or one
 * named outright. Absent on steps authored before the field existed; those
 * resolve as `{ kind: 'active' }`.
 *
 * Strict on the explicit option — persisted authoring intent: "exactly this
 * tooltip" silently becoming the flange would change which tool moves.
 */
export const StepTooltip = zod.union([
  zod.object({ kind: zod.literal('active') }),
  zod.object({
    kind: zod.literal('explicit'),
    tcpOption: TCPOffsetOptionStrict,
    gripperID: zod.string().optional(),
  }),
]);

export type StepTooltip = zod.infer<typeof StepTooltip>;

/**
 * What an absent `stepTooltip` means, for everything that has to name it: the
 * runner resolving one, and the editor displaying one. Shared so a change of
 * mind about the default cannot land on the resolution and miss the display.
 */
export const DEFAULT_STEP_TOOLTIP: StepTooltip = { kind: 'active' };

/**
 * The frame a `StepTooltip` names, given the robot's Active Tooltip. Returns
 * `undefined` when it names `'active'` but no Active Tooltip is known — the
 * caller then falls back to its legacy resolution rather than guessing.
 */
export function resolveStepTooltip(
  stepTooltip: StepTooltip | undefined,
  activeTooltip: TCPFrame,
): TCPFrame;
export function resolveStepTooltip(
  stepTooltip: StepTooltip | undefined,
  activeTooltip: TCPFrame | undefined,
): TCPFrame | undefined;

export function resolveStepTooltip(
  stepTooltip: StepTooltip | undefined,
  activeTooltip: TCPFrame | undefined,
): TCPFrame | undefined {
  const effective = stepTooltip ?? DEFAULT_STEP_TOOLTIP;

  if (effective.kind === 'active') {
    return activeTooltip;
  }

  return { tcpOption: effective.tcpOption, gripperID: effective.gripperID };
}

/**
 * The two fields a Move step's tooltip can be stored in: its own `stepTooltip`,
 * and `constantSpeedTCPOption`, the field the removed constant-speed picker
 * wrote.
 */
export interface StepTooltipFields {
  stepTooltip?: StepTooltip;
  enforceConstantSpeed?: boolean;
  constantSpeedTCPOption?: TCPOffsetOption | 'auto';
}

/**
 * The tooltip a Move step is authored with, reconciling the two fields: the
 * Move step's own tooltip governs wherever it is stored, and the legacy
 * constant-speed field governs the steps that predate it. Neither field is ever
 * rewritten — a step keeps whichever one it was saved with. Naming nothing
 * reads as the Active Tooltip, the same reading an absent `stepTooltip` gets
 * everywhere else.
 *
 * The legacy field carries no `gripperID`: the removed picker never stored one,
 * so it keeps resolving by name against enabled equipment exactly as it did
 * before.
 */
export function getEffectiveStepTooltip({
  stepTooltip,
  enforceConstantSpeed,
  constantSpeedTCPOption,
}: StepTooltipFields): StepTooltip {
  if (stepTooltip) {
    return stepTooltip;
  }

  if (
    enforceConstantSpeed &&
    constantSpeedTCPOption &&
    constantSpeedTCPOption !== 'auto'
  ) {
    return { kind: 'explicit', tcpOption: constantSpeedTCPOption };
  }

  return DEFAULT_STEP_TOOLTIP;
}

/**
 * Whether neither field names a tooltip at all: the step has no `stepTooltip` of
 * its own, and its fields together resolve to the Active Tooltip rather than to
 * a tooltip it named.
 *
 * Defined in terms of `getEffectiveStepTooltip` so the two cannot disagree about
 * which inputs name a tooltip — notably a legacy option left behind on a step
 * whose constant speed was since turned off, which names nothing, nothing
 * reading that field outside constant speed.
 *
 * Distinct from an effective `{ kind: 'active' }`, which a step reaches either by
 * naming the Active Tooltip outright or by naming nothing — only the callers that
 * must tell an unset step from a deliberate one care about the difference, which
 * is why this predicate exists alongside the resolution.
 */
export function namesNoTooltip(fields: StepTooltipFields): boolean {
  return (
    !fields.stepTooltip && getEffectiveStepTooltip(fields).kind === 'active'
  );
}

/**
 * Parse the desired tooltip index from a tcpOption.
 * Returns -1 to indicate tool0 (wrist/wristCamera) or non-EE options.
 * Supports numeric suffixes and named multi-tip devices (primary/secondary).
 */
export function getTCPIndexFromTCPOption(tcpOption: TCPOffsetOption): number {
  if (tcpOption === 'wrist' || tcpOption === 'wristCamera') return -1;
  if (!isEndEffectorTCP(tcpOption)) return -1;

  const parts = tcpOption.split('-');
  const rest = parts.slice(1); // remove 'ee'

  // numeric suffix
  const last = rest[rest.length - 1];
  const numericIndex = Number.parseInt(last, 10);
  if (!Number.isNaN(numericIndex)) return numericIndex;

  // named suffixes for multi-tip devices
  if (last === 'primary') return 0;
  if (last === 'secondary') return 1;

  // default for single-tip named grippers
  return 0;
}
